/**
 * Jubly Calendar Sidebar — Google Calendar style
 * FAB create button, mini calendar, collapsible calendar lists with checkboxes
 */

import { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  Settings
} from 'lucide-react';

// Mini Calendar
function MiniCalendar({ currentDate, onDateSelect, selectedDate }) {
  const [viewDate, setViewDate] = useState(currentDate);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    const days = [];

    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: prevMonth.getDate() - i,
        currentMonth: false,
        fullDate: new Date(year, month - 1, prevMonth.getDate() - i)
      });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: i, currentMonth: true, fullDate: new Date(year, month, i) });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: i, currentMonth: false, fullDate: new Date(year, month + 1, i) });
    }
    return days;
  };

  const days = getDaysInMonth(viewDate);
  const today = new Date();

  const isSameDay = (d1, d2) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();

  return (
    <div className="px-4 pt-2 pb-3">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
        </span>
        <div className="flex">
          <button
            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1))}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
          <button
            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1))}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {dayNames.map((day, i) => (
          <div key={i} className="text-center text-[10px] font-medium text-gray-500 h-6 flex items-center justify-center">
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7">
        {days.map((day, idx) => {
          const isCurrentDay = isSameDay(day.fullDate, today);
          const isSelected = selectedDate && isSameDay(day.fullDate, selectedDate) && !isCurrentDay;
          return (
            <button
              key={idx}
              onClick={() => onDateSelect(day.fullDate)}
              className={`
                w-7 h-7 flex items-center justify-center text-xs rounded-full mx-auto
                transition-colors
                ${!day.currentMonth ? 'text-gray-400' : 'text-gray-700'}
                ${isCurrentDay ? 'bg-blue-600 text-white font-semibold' : ''}
                ${isSelected ? 'bg-blue-100 text-blue-700 font-medium' : ''}
                ${!isCurrentDay && !isSelected ? 'hover:bg-gray-100' : ''}
              `}
            >
              {day.date}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Calendar checkbox item with gear menu
function CalendarCheckItem({ calendar, onToggle, onEdit, onShare, onSubscribe }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex items-center gap-3 py-1 px-2 group rounded-md hover:bg-gray-100 transition-colors relative">
      {/* Checkbox */}
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={calendar.is_visible !== false}
          onChange={() => onToggle(calendar.id, !(calendar.is_visible !== false))}
          className="sr-only"
        />
        <div
          className={`
            w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center flex-shrink-0
            transition-colors
          `}
          style={{
            borderColor: calendar.color || '#3B82F6',
            backgroundColor: calendar.is_visible !== false ? (calendar.color || '#3B82F6') : 'transparent'
          }}
        >
          {calendar.is_visible !== false && (
            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
              <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      </label>

      {/* Label */}
      <span className={`text-sm flex-1 truncate ${calendar.is_visible !== false ? 'text-gray-700' : 'text-gray-400'}`}>
        {calendar.name}
      </span>

      {/* Settings gear (on hover) */}
      <button
        onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
        className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity"
      >
        <Settings className="w-3.5 h-3.5 text-gray-500" />
      </button>

      {/* Dropdown menu */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30 min-w-[160px]">
            <button
              onClick={() => { onEdit(calendar); setMenuOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Settings
            </button>
            {onShare && (
              <button
                onClick={() => { onShare(calendar); setMenuOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Share calendar
              </button>
            )}
            {onSubscribe && (
              <button
                onClick={() => { onSubscribe(calendar); setMenuOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Subscribe (iPhone/Android)
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Collapsible section
function CalendarSection({ title, children, defaultOpen = true, onAdd }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="px-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-2 px-2 group rounded-md hover:bg-gray-100 transition-colors"
      >
        <span className="text-sm font-medium text-gray-600">{title}</span>
        <div className="flex items-center gap-1">
          {onAdd && (
            <span
              onClick={(e) => { e.stopPropagation(); onAdd(); }}
              className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity"
            >
              <Plus className="w-3.5 h-3.5 text-gray-500" />
            </span>
          )}
          {isOpen
            ? <ChevronUp className="w-4 h-4 text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-400" />
          }
        </div>
      </button>
      {isOpen && <div className="pb-2">{children}</div>}
    </div>
  );
}

// Subscribe modal
function SubscribeModal({ calendar, onClose }) {
  const [urls, setUrls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useState(() => {
    fetch(`/api/calendars/${calendar.id}/subscribe`, {
      method: 'POST',
      credentials: 'include'
    })
      .then(r => r.json())
      .then(data => { setUrls(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [calendar.id]);

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Subscribe to Calendar</h3>
        <p className="text-sm text-gray-500 mb-4">
          Add this calendar to your iPhone, Android, or other calendar app.
        </p>

        {loading ? (
          <div className="text-center py-4 text-gray-400">Loading...</div>
        ) : urls ? (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">iPhone / macOS Calendar</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={urls.webcalUrl || ''}
                  className="flex-1 text-xs px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg"
                />
                <button
                  onClick={() => handleCopy(urls.webcalUrl)}
                  className="px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <a href={urls.webcalUrl} className="text-xs text-blue-600 hover:underline mt-1 inline-block">
                Open in Calendar app
              </a>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Android / Google Calendar</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={urls.httpsUrl || ''}
                  className="flex-1 text-xs px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg"
                />
                <button
                  onClick={() => handleCopy(urls.httpsUrl)}
                  className="px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">
              Paste this URL in your calendar app's "Add calendar by URL" option. Events sync automatically.
            </p>
          </div>
        ) : (
          <p className="text-sm text-red-500">Failed to generate subscription URL.</p>
        )}

        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Sidebar
export default function CalendarSidebar({
  calendars,
  sharedCalendars,
  currentDate,
  selectedDate,
  onDateSelect,
  onCalendarToggle,
  onCalendarEdit,
  onCreateCalendar,
  onCreateEvent
}) {
  const [subscribeCalendar, setSubscribeCalendar] = useState(null);

  return (
    <>
    {subscribeCalendar && (
      <SubscribeModal calendar={subscribeCalendar} onClose={() => setSubscribeCalendar(null)} />
    )}
    <div className="w-[256px] bg-white border-r border-gray-200 h-full flex flex-col flex-shrink-0">
      {/* Create button — Google's large pill */}
      <div className="p-4">
        <button
          onClick={onCreateEvent}
          className="
            flex items-center gap-3 px-6 py-3 rounded-2xl
            bg-white border border-gray-200 shadow-md
            hover:shadow-lg hover:bg-gray-50
            transition-all text-gray-700 font-medium text-sm
          "
        >
          <Plus className="w-8 h-8 p-1 text-blue-600" />
          <span>Create</span>
        </button>
      </div>

      {/* Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Mini Calendar */}
        <MiniCalendar
          currentDate={currentDate}
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
        />

        <div className="border-t border-gray-200 mt-2 pt-2">
          {/* My calendars */}
          <CalendarSection title="My calendars" onAdd={onCreateCalendar}>
            {calendars.map(calendar => (
              <CalendarCheckItem
                key={calendar.id}
                calendar={calendar}
                onToggle={onCalendarToggle}
                onEdit={onCalendarEdit}
                onSubscribe={(cal) => setSubscribeCalendar(cal)}
              />
            ))}
          </CalendarSection>

          {/* Other calendars */}
          {sharedCalendars?.length > 0 && (
            <CalendarSection title="Other calendars">
              {sharedCalendars.map(share => (
                <CalendarCheckItem
                  key={share.id}
                  calendar={{
                    id: share.id,
                    name: share.calendar_name,
                    color: share.calendar_color,
                    is_visible: true
                  }}
                  onToggle={() => {}}
                  onEdit={() => {}}
                />
              ))}
            </CalendarSection>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
